import type { FontOption } from 'types/documentServices';

import type { EditorAPI } from '#packages/editorAPI';

import * as textControls from '../../index';
import type {
  FontGroup,
  FontGroupName,
  FontOption as PickerFontOption,
} from './fontPicker';

type Font = FontOption['fonts'][0];

const groupsOrder = new Map(
  [
    'my-uploads',
    'latin',
    'latin-ext',
    'cyrillic',
    'japanese',
    'korean',
    'arabic',
    'hebrew',
  ].map((it, index) => [it, index]),
);

const getGroupOrder = (group: string) =>
  groupsOrder.get(group) ?? Number.MAX_SAFE_INTEGER;

export function getFontGroups(editorAPI: EditorAPI): FontGroup[] {
  let fontsToUse: FontOption[] =
    textControls.fontUtils.getCurrentSelectableFontsWithParams(
      editorAPI,
      false,
      false,
    );
  const uploadedGroup = fontsToUse.find((it) => it.lang === 'my-uploads');
  const latinGroup = fontsToUse.find((it) => it.lang === 'latin');

  const uploadedFontsSet = new Set<string>(
    uploadedGroup?.fonts.map((it) => it.fontFamily),
  );

  if (uploadedGroup && latinGroup) {
    fontsToUse = fontsToUse.filter((it) => it.lang !== 'my-uploads');
    latinGroup.fonts = [...uploadedGroup.fonts, ...latinGroup.fonts];
  }

  const fontsGroups: FontGroup[] = fontsToUse.map((group) => {
    const fonts: PickerFontOption[] = [];

    group.fonts.forEach((font: Font) => {
      if (font.permissions !== 'legacy') {
        // TODO remove all AnyFixMe below when Font type will be updated in DM
        const fontFixed = font as AnyFixMe;
        const styles = fontFixed.style ?? [];
        const nativeFontWeights = styles
          .filter((s: AnyFixMe) => s.style === 'normal')
          .map((s: AnyFixMe) => s.weight);
        const weights = nativeFontWeights.length ? nativeFontWeights : [400];

        fonts.push({
          label: font.displayName,
          family: font.fontFamily,
          isUploaded: uploadedFontsSet.has(font.fontFamily),
          cssFontFamily: getCSSFontFamily(font),
          weights,
        });
      }
    });

    return {
      name: group.lang as FontGroupName,
      visible: true,
      fonts,
    };
  });

  return fontsGroups.sort(
    (a, b) => getGroupOrder(a.name) - getGroupOrder(b.name),
  );
}

function getCSSFontFamily(font: Font): string {
  let css = font.fontFamily;

  if (font.fallbacks) {
    css += `,${font.fallbacks}`;
  }

  if (font.genericFamily) {
    css += `,${font.genericFamily}`;
  }

  return css;
}
